import { env } from '@/env';
import { getQcpToken, hasStoredSession } from '@/lib/logto';

/**
 * Centralised QChinesePoker (QCP) gameserver auth.
 *
 * Mirrors the resolution order established by qgammon/auth.ts, adapted for
 * the QCP gameserver whose Socket.IO middleware reads:
 *
 *   socket.handshake.auth?.token   (→ "Bearer <token>" internally)
 *   socket.handshake.auth?.devBypass
 *   socket.handshake.auth?.user    (dev identity override)
 *
 * NOTE on the `token` field: the QCP gameserver reads `auth.token` (NOT
 * `auth.accessToken` like QGammon). This is intentional — the two gameservers
 * diverged here; NEVER rename this field without updating the server at
 * `QChinesePoker/apps/qcp-gameserver/src/server.ts:102`.
 *
 * RESOLUTION ORDER (shared by REST and the Socket.IO handshake):
 *   1. A real Logto access token scoped to the QCP API resource
 *      — the production / signed-in path, per-user identity.
 *      Until the "Qlash API QChinesePoker" resource is registered in Logto
 *      and granted to the TMA app, this path returns null and the token field
 *      comes back as an empty string (the gameserver answers 401, which is the
 *      correct dark/unreachable state during that period).
 *   2. The dev-bypass env (`env.qcpDevBypass` + optional `env.qcpDevUser`)
 *      — dev-only escape hatch, gated to dev in env.ts; never set in prod.
 *      ONLY used when there is NO Logto session (`!hasStoredSession()`), the
 *      same guard qgammon/auth.ts already carries. If the user IS signed in but
 *      the QCP token mint failed, we deliberately do NOT fall back: masking a
 *      real signed-in user as the shared dev identity is what hides dead-grant
 *      and missing-resource-grant failures instead of surfacing them.
 *   3. Neither → no auth material. The gameserver answers 401, which the
 *      caller surfaces as a connection error.
 *
 * Logto: the "Qlash API QChinesePoker" resource is registered in PROD Logto
 * (2026-06-17, aud https://qchinesepoker.qlash.gg/api), so it is wired into
 * `src/lib/logto.ts` (`logtoConfig.resources` + `buildAuthUrl`) and
 * getQcpToken() mints a per-user access token for it. (Golden rule satisfied:
 * adding an UNREGISTERED resource to the authorize request would cause
 * `invalid_target` and break ALL sign-in — that risk is now moot.) A signed-in
 * user therefore gets a real per-user identity; dev-bypass remains the fallback
 * for unauthenticated dev builds.
 */

/**
 * The Socket.IO handshake `auth` object for the QCP gameserver:
 * `{ token }` when a Logto token is available, else `{ devBypass, devUser? }`,
 * else `{}`.
 *
 * The gameserver middleware reads `socket.handshake.auth.token` first (not
 * `accessToken`), then falls back to `devBypass`/`user`. See
 * `QChinesePoker/apps/qcp-gameserver/src/server.ts:100–115`.
 */
export async function qcpSocketAuth(): Promise<Record<string, string>> {
  // 1. Real per-user Logto token (production / signed-in path). aud
  //    https://qchinesepoker.qlash.gg/api; null when not signed in / grant
  //    missing → fall through to the dev-bypass below.
  const token = await getQcpToken();

  if (token) {
    return { token };
  }

  // 2. Dev-bypass fallback — ONLY when there's no real session (anonymous dev).
  //    A signed-in user whose token mint failed must NOT become the shared dev
  //    identity: that turns an auth failure into a silent wrong-identity, which
  //    is precisely the bug qgammon/auth.ts was fixed for.
  const auth: Record<string, string> = {};
  if (env.qcpDevBypass && !hasStoredSession()) {
    auth.devBypass = env.qcpDevBypass;
    if (env.qcpDevUser) auth.devUser = env.qcpDevUser;
  }

  // 3. Neither: empty handshake (the server will reject the connection).
  return auth;
}

/**
 * Headers for a REST call to the QCP gameserver (:4101) or qcp-api (:4100):
 * `Authorization: Bearer <logto>` when a token is available, else the
 * `X-Dev-Bypass` (+ optional `X-Dev-User`) dev headers, else nothing.
 *
 * Pass `json: true` to also set `Content-Type: application/json` for a body.
 */
export async function qcpAuthHeaders(json = false): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  if (json) headers['Content-Type'] = 'application/json';

  // 1. Real per-user Logto token (production / signed-in path). Minted by
  //    getQcpToken() (aud https://qchinesepoker.qlash.gg/api); null when not
  //    signed in / grant missing → fall through to the dev-bypass below.
  const token = await getQcpToken();

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  // 2. Dev-bypass fallback — ONLY when there's no real session (see above).
  if (env.qcpDevBypass && !hasStoredSession()) {
    headers['X-Dev-Bypass'] = env.qcpDevBypass;
    if (env.qcpDevUser) headers['X-Dev-User'] = env.qcpDevUser;
  }

  // 3. Neither: return as-is (the server will answer 401).
  return headers;
}
